// Owner-facing explanations for why automatic dispatch did not give a parcel a
// Rider. Shared by Delivery Operations and the order's handoff step so both say
// the same thing. Codes are never shown; an unknown one gets no invented cause.
export const DISPATCH_REASON_COPY = Object.freeze({
  NO_ELIGIBLE_RIDER: 'No rider is available for this parcel right now. Check that your riders are on duty and have room for more parcels.',
  // Why the best-placed Rider was refused at the last step. These are schedule
  // problems an owner can fix in HR, not riders being full.
  RIDER_WORK_WINDOW_UNAVAILABLE: 'The rider who could take this parcel has no shift scheduled right now. Assign them a shift in HR > Shifts.',
  WORK_WINDOW_OVERRUN: 'The delivery would run past the end of the rider’s shift. Extend the shift in HR > Shifts, or it will be dispatched in the next shift.',
  RIDER_NOT_ASSIGNMENT_ELIGIBLE: 'The rider who could take this parcel is still on another trip. It will be dispatched when they are free.',
  MANUAL_REVIEW_ROUTE_UNAVAILABLE: 'Route planning was unavailable',
  INPUT_CHANGED_AT_COMMIT: 'Delivery details changed during assignment',
  DELIVERY_UNRESOLVED_OBLIGATIONS: 'The rider who could take this parcel is still holding cash or parcels from an earlier trip. Once they hand them over and you accept them at the settlement counter, this parcel is dispatched.',
  RIDER_WORK_HOLD_OPEN: 'The rider who could take this parcel has a cash count that is still being looked into. Resolve it in Delivery Settlement.',
  // These two are store setup, not rider availability. Until now they fell
  // through to the catch-all below and sent owners hunting for a rider problem
  // that did not exist.
  DELIVERY_COMMITMENT_POLICY_UNAVAILABLE: 'This store has no delivery rules yet. Save a confirmed dispatch location in Delivery Setup, which creates them.',
  DELIVERY_COMMITMENT_POLICY_INVALID: 'This store’s delivery rules could not be read. Review them in Delivery Setup.',
  SETTLEMENT_WINDOW_UNAVAILABLE: 'The trip would end outside the receiving days or hours set in Delivery Setup. Check both: today must be a receiving day, and the hours must cover the ride back.',
  DELIVERY_ORIGIN_REQUIRED: 'This store has no confirmed dispatch location. Set one in Delivery Setup.',
  DELIVERY_FEE_POLICY_REQUIRED: 'This store has no active delivery pricing. Set it in Delivery Setup.',
});

// Where an owner goes to fix each cause. Rider-schedule problems live in HR;
// store setup lives in Delivery Setup; everything else is the Riders view.
const FIX_LINKS = Object.freeze({
  RIDER_WORK_WINDOW_UNAVAILABLE: { to: '/business/hr/shifts', label: 'Open HR shifts' },
  WORK_WINDOW_OVERRUN: { to: '/business/hr/shifts', label: 'Open HR shifts' },
  DELIVERY_COMMITMENT_POLICY_UNAVAILABLE: { to: '/business/settings?tab=delivery', label: 'Open Delivery Setup' },
  DELIVERY_COMMITMENT_POLICY_INVALID: { to: '/business/settings?tab=delivery', label: 'Open Delivery Setup' },
  SETTLEMENT_WINDOW_UNAVAILABLE: { to: '/business/settings?tab=delivery', label: 'Open Delivery Setup' },
  DELIVERY_ORIGIN_REQUIRED: { to: '/business/settings?tab=delivery', label: 'Open Delivery Setup' },
  DELIVERY_FEE_POLICY_REQUIRED: { to: '/business/settings?tab=delivery', label: 'Open Delivery Setup' },
  // Cash or parcels a rider still holds are settled at the counter.
  DELIVERY_UNRESOLVED_OBLIGATIONS: { to: '/business/delivery/settlement', label: 'Open Delivery Settlement' },
  RIDER_WORK_HOLD_OPEN: { to: '/business/delivery/settlement', label: 'Open Delivery Settlement' },
});

function clock(value, timeZone) {
  return new Intl.DateTimeFormat('en-PH', { hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(value));
}

// F9: one answer for a refusal caused by the clock. Both limits (the Rider's
// shift and the store's receiving hours) and the latest safe departure for this
// parcel's own trip, instead of meeting one rule, fixing it, then the next.
export function dispatchTimeExplanation(timeWindow, now = new Date()) {
  if (!timeWindow) return null;
  const tz = timeWindow.timezone || undefined;
  const minutes = Math.max(1, Math.round(Number(timeWindow.tripSeconds || 0) / 60));
  const trip = `about ${minutes} min there and back`;
  if (!timeWindow.receivingDay) {
    return `New trips can't leave today: the store isn't receiving returning Riders today. This trip takes ${trip}.`;
  }
  const closes = `the store receives returning Riders until ${clock(timeWindow.receivingClosesAt, tz)}`;
  if (!timeWindow.riderShiftEndsAt) {
    return `New trips can't leave today: no Rider has a shift today, and ${closes}.`;
  }
  const shift = `Rider shifts today end at ${clock(timeWindow.riderShiftEndsAt, tz)}`;
  if (!timeWindow.latestDepartureAt) {
    return `New trips can't leave today: ${shift} and ${closes}, which leaves no time for this trip (${trip}).`;
  }
  const latest = clock(timeWindow.latestDepartureAt, tz);
  if (timeWindow.tooLateToday || new Date(timeWindow.latestDepartureAt) < now) {
    return `New trips can no longer leave today. ${shift} and ${closes}. Based on this trip (${trip}), the latest safe departure was about ${latest}.`;
  }
  if (!timeWindow.ridersOnShiftNow) {
    return `No Rider is on shift right now. ${shift} and ${closes}; this trip (${trip}) can leave until about ${latest}.`;
  }
  return `${shift} and ${closes}; this trip (${trip}) can leave until about ${latest}.`;
}

export function dispatchReasonCopy(code) {
  return DISPATCH_REASON_COPY[code] || 'Automatic dispatch could not give this parcel a rider yet.';
}

export function dispatchReasonFix(code) {
  return FIX_LINKS[code] || { to: '/business/delivery?view=riders', label: 'Check riders' };
}

// The store's receiving windows, said the way an owner set them. Days use 0 =
// Sunday, as in Delivery Setup. Used next to SETTLEMENT_WINDOW_UNAVAILABLE so the
// owner sees the setting that is refusing, not just that one is.
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

function describeDays(days) {
  const set = new Set((days || []).map(Number));
  const ordered = WEEK_ORDER.filter((day) => set.has(day));
  if (ordered.length === 7) return 'Every day';
  // Collapse consecutive runs in Mon..Sun order: Mon-Fri, Sat.
  const runs = [];
  for (const day of ordered) {
    const last = runs[runs.length - 1];
    if (last && WEEK_ORDER.indexOf(day) === WEEK_ORDER.indexOf(last[last.length - 1]) + 1) last.push(day);
    else runs.push([day]);
  }
  return runs.map((run) => (run.length > 2 ? `${DAY_SHORT[run[0]]}–${DAY_SHORT[run[run.length - 1]]}` : run.map((d) => DAY_SHORT[d]).join(', '))).join(', ');
}

export function describeReceivingWindows(windows = []) {
  return windows.map((window) => `${describeDays(window.daysOfWeek)}, ${window.opensAt}–${window.closesAt}`).join('; ');
}

export function receivingDayNote(windows = [], now = new Date()) {
  const today = now.getDay();
  const open = windows.some((window) => (window.daysOfWeek || []).map(Number).includes(today));
  return open ? null : `Today (${DAY_LONG[today]}) is not a receiving day, so nothing can be dispatched today until you tick it.`;
}
